import { Button, Stack, Typography } from '@mui/material'
import { useEffect, useState } from 'react'
import { Metric, MetricKind, MetricSet } from 'api/model'
import { useRecoilState, useSetRecoilState } from 'recoil'
import { organizationIdAtom, toastAtom } from 'state'
import { listMetricsValues, calculateMetrics, downloadMetrics } from 'api/endpoints/jelledFromFileSpecWithTransformer'
import { useActiveOrganization } from 'hooks/use-organization'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import { format } from 'date-fns'
import useCheckMobileScreen from 'hooks/use-check-mobile-screen'
import OrganizationSelector from './OrganizationSelector'
import MetricSetSelector from './MetricSetSelector'

const AIMetrics = () => {
  const isMobile = useCheckMobileScreen()

  const [activeOrganization] = useActiveOrganization()
  const setToast = useSetRecoilState(toastAtom)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const [metrics, setMetrics] = useState<Metric[]>()
  const [metricSet, setMetricSet] = useState<MetricSet>(MetricSet.all)

  const [organizationId, setOrganizationId] = useRecoilState(organizationIdAtom)

  // whenever we select a specific organization, we want to set metricsSet to be MetricSet.all
  useEffect(() => {
    if (organizationId) {
      setMetricSet(MetricSet.all)
    }
  }, [organizationId])

  useEffect(() => {
    const fetchMetrics = async () => {
      if (!activeOrganization) {
        return
      }
      try {
        const results = await listMetricsValues(activeOrganization, {
          metric_kind: MetricKind.jelled_ai,
          metric_set: metricSet,
          latest_only: true,
          include_description: true,
          metric_org: organizationId || undefined,
        })
        setMetrics(results.data)
      } catch (Error) {
        setToast({ title: 'Metrics fetch ', severity: 'error' })
      }
    }
    fetchMetrics()
  }, [activeOrganization, setToast, organizationId, metricSet])

  const generateMetrics = async () => {
    if (!activeOrganization || isSubmitting) {
      return
    }
    try {
      setIsSubmitting(true)

      const results = await calculateMetrics(activeOrganization, {
        metric_kind: MetricKind.jelled_ai,
        metric_set: metricSet,
        include_description: true,
        metric_org: organizationId || undefined,
      })
      setMetrics(results.data)
    } catch (Error) {
      setToast({ title: 'Metrics fetch ', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  const retrieveMetrics = async () => {
    if (!activeOrganization || isSubmitting) {
      return
    }
    try {
      setIsSubmitting(true)

      const results = await downloadMetrics(activeOrganization, {
        metric_kind: MetricKind.jelled_ai,
        metric_set: metricSet,
        metric_org: organizationId || undefined,
      })

      var a = document.createElement('a')
      if (a != null && a instanceof HTMLAnchorElement) {
        const f = new Blob([results.data], { type: 'text/csv' })
        a.href = URL.createObjectURL(f)

        a.download = 'jelled_ai_metrics_' + format(new Date(), `yyyyMMdd_hhmmss`) + '.csv'
        a.click()
        a.remove()
      }
    } catch (Error) {
      setToast({ title: 'Metrics download ', severity: 'error' })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Stack direction="column" spacing={4}>
      <Stack
        direction={isMobile ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isMobile ? 'flex-start' : 'center'}>
        <Stack direction="column">
          {!isMobile && (
            <Typography variant="h4" mb={0}>
              AI metrics
            </Typography>
          )}
          {metrics && metrics.length > 0 && (
            <Typography variant="pXS" mb={isMobile ? 2 : 0}>
              Last generated on {format(new Date(metrics[0].date_posted), `MMM d, y 'at' h:mm a`)}
            </Typography>
          )}
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button variant="contained" color="secondary" disabled={isSubmitting} onClick={() => retrieveMetrics()}>
            Export
          </Button>
          <Button variant="contained" color="primary" disabled={isSubmitting} onClick={() => generateMetrics()}>
            Generate Report
          </Button>
        </Stack>
      </Stack>

      <OrganizationSelector organizationId={organizationId} setOrganizationId={setOrganizationId} />
      <MetricSetSelector metricSet={metricSet} setMetricSet={setMetricSet} />
      <TableContainer component={Paper} elevation={0} sx={{ maxWidth: isMobile ? '100%' : '50%', boxShadow: 'none' }}>
        <Table size="small" aria-label="metrics table">
          <caption>
            <Typography variant="pXS">
              These data exclude activity within domains owned by Jelled as well as activities performed by Jelled
              employees on behalf of customers
            </Typography>
          </caption>
          <TableBody>
            {metrics &&
              metrics.map((m) => (
                <TableRow key={m.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell
                    align="left"
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                    }}>
                    {m.description}
                  </TableCell>
                  <TableCell
                    align="left"
                    style={{
                      fontSize: isMobile ? '14px' : '16px',
                      fontWeight: 'bold',
                    }}>
                    {m.string_value}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Stack>
  )
}

export default AIMetrics
